import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { date as fmtDate } from '@/lib/format';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { StatusChip } from '@/components/ui/StatusChip';
import { STATUS_LABEL, STATUS_TONE, WORKSHOP_ROLES, cardForBreakdown } from '@/lib/workshop';
import type { JobCard } from '@/types/models';
import { useBreakdownCard } from './useBreakdownCard';

/** Where the workshop is with a vehicle, in one line — for the branch. */
export function JobStatusLine({ card }: { card: JobCard | undefined }) {
  if (!card) return null;
  return (
    <span className="job-inline small" data-job={card.job_no}>
      Workshop {card.job_no}
      <StatusChip tone={STATUS_TONE[card.status]}>{STATUS_LABEL[card.status]}</StatusChip>
      {card.due_date && ['open', 'in_progress', 'awaiting_parts'].includes(card.status) && (
        <span className="muted">expected {fmtDate(card.due_date)}</span>
      )}
    </span>
  );
}

/**
 * On a breakdown: its job card, or — for the workshop — a button that
 * opens one, filled in from the breakdown.
 */
export function BreakdownJobCard({ breakdown }: {
  breakdown: { id: string; machine_id: string; failure_description?: string | null; severity?: string | null;
               hour_meter_at_failure?: number | null; status?: string | null };
}) {
  const card = useBreakdownCard(breakdown.id);
  const profile = useAuthStore((s) => s.profile);
  const workshop = !profile?.home_site_id && WORKSHOP_ROLES.includes(profile?.role as never);
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const closed = ['closed', 'cancelled'].includes(String(breakdown.status ?? ''));

  async function open() {
    setBusy(true);
    try {
      const c = await cardForBreakdown(card, breakdown);
      if (!card) toast.success(`Job card ${c.job_no} opened`);
      nav(`/workshop?card=${c.id}`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!card && (!workshop || closed)) return null;
  return (
    <div className="row job-link" style={{ gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap', marginBottom: 'var(--sp-3)' }}>
      {card ? <JobStatusLine card={card} /> : <span className="small muted">No job card yet.</span>}
      {workshop && (
        <Button variant="secondary" loading={busy} onClick={() => void open()}>
          {card ? 'Open job card' : 'Open a job card'}
        </Button>
      )}
    </div>
  );
}
